import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
import classNames from "classnames";

/**
 * Get Involved page component
 * @returns {JSX.Element}
 * @component
 */
const GetInvolved = () => {
  const [loader, setLoader] = useState(true);
  const sectionRefs = useRef({});
  const {
    home: homeData,
    membership: { data: membershipData },
  } = useSelector((state) => state.pageContents);

  // Initialize loading state
  useEffect(() => {
    const timer = setTimeout(() => setLoader(false), 1500);

    // Handle hash navigation
    const hash = window.location.hash;
    const sectionId = hash.substring(1);
    if (sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        scrollToSection(element);
      }
    }

    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (elem) => {
    window.scrollTo({
      top: elem.offsetTop - 50,
      behavior: "smooth",
    });
  };

  return (
    <PageLayout pageName="Get Involved" showLoader={loader} checkComingSoon>
      {/* Banner  */}
      <PageBanner
        bannerImage={membershipData?.getInvolvedbanner?.banner_image}
        mainTitle={
          membershipData?.getInvolvedbanner?.banner_title || "Get Involved"
        }
        pageTitle="Membership"
      />

      {/* Main Content  */}
      {(membershipData?.getInvolved || homeData?.involvedata?.length > 0) && (
        <Box component="section" sx={{ py: 6 }}>
          <Container>
            {/* Intro Section */}
            {membershipData?.getInvolved && (
              <Box sx={{ mb: 6, textAlign: "center" }}>
                <Typography variant="h4" component="h1" gutterBottom>
                  {membershipData.getInvolved.get_involved_section_title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: "900px",
                    mx: "auto",
                    mb: 4,
                  }}
                >
                  {membershipData.getInvolved.get_involved_des}
                </Typography>
              </Box>
            )}

            {/* Involvement Items  */}
            {homeData?.involvedata?.map((item, index) => (
              <Box
                key={index}
                id={`getinvolved-${item.involve_id}`}
                ref={(el) =>
                  (sectionRefs.current[`getinvolved-${item.involve_id}`] = el)
                }
                sx={{
                  py: 4,
                  bgcolor: index % 2 === 0 ? "background.default" : "#f9fbfc",
                  mb: 4,
                }}
                className={classNames({
                  "involve-sec": index % 2 === 0,
                  "last-getinvol": index === homeData.involvedata.length - 1,
                })}
              >
                <Container>
                  <Grid
                    container
                    spacing={4}
                    alignItems="center"
                    direction={index % 2 === 0 ? "row" : "row-reverse"}
                  >
                    {/* Image Section */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box
                        component="img"
                        src={item.involve_image}
                        alt={item.involve_title}
                        sx={{
                          width: "100%",
                          borderRadius: 2,
                          boxShadow: 3,
                        }}
                        className={classNames("involve-img", {
                          "img-top-getinvol": true,
                        })}
                      />
                    </Grid>

                    {/* Content Section */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box
                        className={classNames({
                          "getinvol-odd": index % 2 !== 0,
                          "getinvol-even": index % 2 === 0,
                        })}
                      >
                        <Typography
                          variant="h4"
                          component="h2"
                          gutterBottom
                          className="about-mid-cont about-mid-conts"
                        >
                          {item.involve_title}
                        </Typography>

                        <Typography variant="body1" paragraph>
                          {item.involve_description}
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          gutterBottom
                        >
                          {item.involve_sub_title}
                        </Typography>

                        {/* List Items  */}
                        <List disablePadding sx={{ mb: 3 }}>
                          {item.involve_first && (
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={item.involve_first} />
                            </ListItem>
                          )}
                          {item.involve_second && (
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={item.involve_second} />
                            </ListItem>
                          )}
                          {item.involve_third && (
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={item.involve_third} />
                            </ListItem>
                          )}
                          {item.involve_four && (
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={item.involve_four} />
                            </ListItem>
                          )}
                          {item.involve_five && (
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={item.involve_five} />
                            </ListItem>
                          )}
                          {item.involve_six && (
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={item.involve_six} />
                            </ListItem>
                          )}
                        </List>

                        {/* Join Now Button */}
                        <Button
                          component={Link}
                          to="/signup"
                          variant="contained"
                          color="primary"
                          sx={{ mt: 2 }}
                          className="default-btn"
                        >
                          Join Now
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            ))}

            {/* CTA Section */}
            <Paper
              elevation={1}
              sx={{
                p: 4,
                mt: 4,
                textAlign: "center",
                borderRadius: 2,
              }}
            >
              <Typography variant="h5" gutterBottom>
                Ready to make an impact?
              </Typography>
              <Typography variant="body1" paragraph>
                Join our community and help make a difference.
              </Typography>
              <Button
                component={Link}
                to="/signup"
                variant="contained"
                color="primary"
                size="large"
              >
                Get Involved Today
              </Button>
            </Paper>
          </Container>
        </Box>
      )}
    </PageLayout>
  );
};

export default GetInvolved;
